import React, {
  FC,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { styled } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Api from '~/Api';
import { useModalContext } from '~/contexts/modal/ModalContext';
import { getToolTitle } from '~/helpers/translation';
import useErrors from '~/hooks/useErrors';
import { DOWNSELL_PLAN_IDS } from '~/modules/cancellation/components/DownsellModal/constants';
import DownsellSuccess from '~/modules/cancellation/components/DownsellModal/DownsellSuccess';
import useDownsell, {
  DiscountOffer,
} from '~/modules/cancellation/components/DownsellModal/useDownsell';
import { ChatToolId } from '~/modules/chat/constants';
import { SubscriptionPath } from '~/modules/settings/constants';
import projectConfig, { PROJECT } from '~/projects/config';
import analytics from '~/services/analytics';
import { useActions } from '~/store/hooks/useActions';
import { useAppSelector } from '~/store/hooks/useAppSelector';
import { colors } from '~/theme/colors';
import { SUBSCRIPTION_STATUS } from '~/types/subscription';
import { timeout } from '~/utils/common';

import { getFormOptions } from './constants';
import FeedbackStep from './FeedbackStep';
import ReasonStep from './ReasonStep';
import styles from './styles.module.scss';
import { CancellationFormDataType, FormFields } from '../../types';

const StyledFormControl = styled(FormControl)(() => ({
  width: '100%',
}));

export enum FORM_STEP {
  REASON = 'reason',
  FEEDBACK = 'feedback',
}

const initialFormState = {
  [FormFields.REASON]: '',
  [FormFields.SUB_REASON]: '',
  [FormFields.RELATED_TO]: '',
  [FormFields.FEEDBACK]: '',
};

type Props = {
  title: string;
  onFormSubmit: (data: CancellationFormDataType) => Promise<void>;
  onFormRefuse: () => void;
};

const FormMultiple: FC<Props> = ({ title, onFormSubmit, onFormRefuse }) => {
  const [form, setForm] = useState<CancellationFormDataType>(initialFormState);
  const [relations, setRelations] = useState<{ text: string; value: string }[]>(
    [],
  );
  const [currentStep, setCurrentStep] = useState(FORM_STEP.REASON);
  const [isDownsellDeclined, setIsDownsellDeclined] = useState(false);

  const { t } = useTranslation('cancellation');
  const scrollElemRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const { reportUserErrors } = useErrors();
  const { loadAndSetActualProfileData } = useActions();
  const { invokeDownsellModal } = useDownsell();
  const { user_product, user_subscription, is_stripe_user } = useAppSelector(
    (state) => state.profile,
  );
  const { handleOpenModal } = useModalContext();

  const formOptions = getFormOptions(t);

  const isDownsellEligible =
    projectConfig.project === PROJECT.JUSTDONE &&
    !is_stripe_user &&
    user_subscription.status === SUBSCRIPTION_STATUS.ACTIVE &&
    user_product.has_trial_period &&
    user_product.id !== DOWNSELL_PLAN_IDS.discount_50;

  const handleProductSwitch = async (productId: string): Promise<void> => {
    try {
      await Api.updateUserProduct(productId);
      await timeout(4000);
      await loadAndSetActualProfileData();
      navigate(SubscriptionPath);
      handleOpenModal({
        maxWidth: '500px',
        closeButtonColor: colors.neutral_30,
        disableBackdropClick: true,
        onShow: () => {
          analytics.trackEvent('subscription - downsell modal success show');
        },
        component: ({ onClose }) => (
          <DownsellSuccess
            onSubmit={() => {
              analytics.trackEvent(
                'subscription - downsell modal success click',
              );
              onClose();
            }}
          />
        ),
      });
    } catch (e) {
      reportUserErrors({
        error: e,
        method: 'updateUserProduct',
        userMessage: t(
          'error:update_user_product',
          'We couldn’t update your subscription. Please try again later or',
        ),
      });
    }
  };

  const handleDiscountButtonClick = async (): Promise<void> => {
    analytics.trackEvent('subscription - cancel form click downsell');

    invokeDownsellModal({
      discountOffer: DiscountOffer.D_50,
      onSubmit: async (productId: string) => {
        await handleProductSwitch(productId);
      },
      onDecline: () => {
        setIsDownsellDeclined(true);
      },
    });
  };

  const handleGoToNextStep = (
    data: Partial<CancellationFormDataType>,
    step: FORM_STEP,
  ): void => {
    setForm((prev) => ({ ...prev, ...data }));
    setCurrentStep(step);

    scrollElemRef.current?.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    (async (): Promise<void> => {
      try {
        const { user_last_tools, user_popular_tool } =
          await Api.getUsageStatistics();

        const relations: { text: string; value: string }[] = [];

        if (user_popular_tool) {
          relations.push({
            text: getToolTitle(user_popular_tool.id, user_popular_tool.name, t),
            value: user_popular_tool.name,
          });
        }

        if (user_last_tools.length > 0) {
          user_last_tools.slice(0, 2).forEach((tool) => {
            if (user_popular_tool && tool.id === user_popular_tool.id) {
              return;
            }
            if (tool.id === ChatToolId) {
              relations.push({
                text: t('general:chat', 'Chat'),
                value: 'Chat',
              });
              return;
            }
            if (relations.length <= 1) {
              relations.push({
                text: getToolTitle(tool.id, tool.title, t),
                value: tool.title,
              });
            }
          });
        }

        if (relations.length > 0) {
          setRelations([
            ...relations,
            { text: t('general:other'), value: 'Other' },
          ]);
        }
      } catch (e: any) {
        reportUserErrors({
          error: e,
          method: 'getUsageStatistics',
          userMessage: t('get_usage_statistics', {
            ns: 'error',
            defaultValue:
              'Usage statistics are currently unavailable. Please try later or',
          }),
        });
      }
    })();
  }, []);

  useEffect(() => {
    if (!isDownsellEligible || isDownsellDeclined) {
      return;
    }

    invokeDownsellModal({
      discountOffer:
        user_product.id !== DOWNSELL_PLAN_IDS.discount_30
          ? DiscountOffer.D_30
          : DiscountOffer.D_50,
      onSubmit: async (productId: string) => {
        await handleProductSwitch(productId);
      },
      onDecline: () => {
        setIsDownsellDeclined(true);
      },
    });
  }, [isDownsellEligible, isDownsellDeclined]);

  const STEPS: Record<FORM_STEP, ReactElement<any, any>> = useMemo(() => {
    return {
      [FORM_STEP.REASON]: (
        <ReasonStep
          title={title}
          options={formOptions}
          relations={relations}
          onRefuse={onFormRefuse}
          onSubmit={(data): void =>
            handleGoToNextStep(data, FORM_STEP.FEEDBACK)
          }
          {...(isDownsellEligible && isDownsellDeclined
            ? {
                onProductSwitch: handleDiscountButtonClick,
              }
            : {})}
        />
      ),
      [FORM_STEP.FEEDBACK]: (
        <FeedbackStep
          title={
            formOptions[form.reason]?.feedbackText ||
            t('v3.default.feedback_title', {
              ns: 'cancellation',
              defaultValue: 'Tell us more about your experience.',
            })
          }
          onRefuse={onFormRefuse}
          onSubmit={(data): Promise<void> =>
            onFormSubmit({
              ...form,
              [FormFields.FEEDBACK]: data.feedback,
            })
          }
          {...(isDownsellEligible && isDownsellDeclined
            ? {
                onProductSwitch: handleDiscountButtonClick,
              }
            : {})}
        />
      ),
    };
  }, [form, relations, isDownsellEligible, isDownsellDeclined]);

  return (
    <div className={styles.container} ref={scrollElemRef}>
      <StyledFormControl error={!form.reason}>
        {STEPS[currentStep]}
      </StyledFormControl>
    </div>
  );
};

export default FormMultiple;
